import { ChangeDetectorRef, Component, Input, ChangeDetectionStrategy, Output, EventEmitter, ViewChild, Injector, HostListener } from '@angular/core';
import { GridColumn } from '../grid-column';
import { FormFieldSettings } from '../../forms/form-field/data/form-field-settings';
import { FieldType } from '../../field-type';
import { formatInteger } from '../../forms/forms-util'
import { TranslateService } from '@ngx-translate/core';
import { ChangeCause } from '../../change-cause';
import { asyncForEach } from '../../../../../../submodules/common/util';
import { BaseView } from '../../../../submodules/angular-ui-common/base-view';
import { FilterFieldState } from '../filter-field/data/filter-field-state';
import { GridSettings } from '../grid-settings';
import { CdkVirtualScrollViewport, ScrollDispatcher } from '@angular/cdk/scrolling';
import { filter } from 'rxjs/operators';
import { FileDto, GridAction } from '../../base-types';
import { FormFieldDataService } from '../../forms/form-field/form-field-data.service';
import { NotificationDataService } from '../../notification-data.service';
import { serverSideGridMaxRowNum } from 'src/app/submodules/angular-common/app-config';
import { SecurityDataService } from 'src/app/submodules/angular-common/security-data.service';
import { Scroll } from '@angular/router';

export interface ParentControlledGridFields {
  workingWidth?: number,
  error?: boolean,   //Indica se si è verificato un errore nel caricamento dei dati
  loading?: boolean, //Riferito al caricamento dell'intera griglia (non solo degli items)
  filtering?: boolean,
  sorting?: boolean,
  loadingMoreItems?: boolean,
  //submitting? booelan; //TODO: potra' servire per datagrid editabile
  rowCount?: number,
  selectionColumnShown?: boolean,
  currentAction?: GridAction | 'none'
}

export interface GridEvent {
  actionChange?: 'none' | GridAction,                                                             //the current action
  widthChange?: number,
  rowClick?: {[key: string]: number | string | boolean | (string |number)[]  | File | File[]} //the selected row
  moreItemsRequest?: number //row number dell'ultimo item caricato
  searchCriteriaChange?: {sort: {columnId: string, direction: 'asc' | 'desc'}, 
                          filter: {[key: string]: {mode:'equal'|'between',equal:any,from:any,to:any}}},
  checkedRowsChange?: string[], // id delle righe selezionate per operazioni multiple
  refreshRequest?: true
}

@Component({
  selector: 'app-grid',
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GridComponent extends BaseView {

 /** 
   * @updatableInput
   * Valori di competenza del consumer. Vengono passati alla griglia attraverso questo oggetto
   */
  @Input() set parentControlledFields(parentControlledFields: ParentControlledGridFields) {
    if(parentControlledFields){
      console.log('parentControlledFields :>> ', JSON.stringify(parentControlledFields));
      if (![null, undefined].includes(parentControlledFields.error))
        this._error = parentControlledFields.error;
      if (![null, undefined].includes(parentControlledFields.loading)){
        this._loading = parentControlledFields.loading;
      }
      if (![null, undefined].includes(parentControlledFields.filtering)) {
        this.rowsShown = !parentControlledFields.filtering;
        this._filtering = parentControlledFields.filtering;
      }
      if (![null, undefined].includes(parentControlledFields.sorting))
        this._sorting = parentControlledFields.sorting;
      if (![null, undefined].includes(parentControlledFields.loadingMoreItems)) {
        console.log('!!!!!!!!gridComponent - loadingMoreItems :>> ', parentControlledFields.loadingMoreItems);
        this._loadingMoreItems = parentControlledFields.loadingMoreItems;
      }
      if (![null, undefined].includes(parentControlledFields.rowCount))
        this._rowCount = parentControlledFields.rowCount;
      if (![null, undefined].includes(parentControlledFields.selectionColumnShown)){
        parentControlledFields.selectionColumnShown?this.showSelectionColumn():this.hideSelectionColumn()
      }
      if (![null, undefined].includes(parentControlledFields.workingWidth)) {
        this._workingWidth = parentControlledFields.workingWidth;
        this._calcWorkingWidthUsedWidth()
      }
      if (![null, undefined].includes(parentControlledFields.currentAction)) {
        this.currentAction = parentControlledFields.currentAction;
        this.eventEmission.emit({actionChange: parentControlledFields.currentAction});
      }
    }
  }

  @Output() eventEmission: EventEmitter<GridEvent> = new EventEmitter<GridEvent>();

  /**
   * Larghezza utile comunicata dal parent in modo che il componente sappia quanto spazio ha a disposizione prima di dover ricorrere allo scrolling
   */
  get workingWidth(){
    return this._workingWidth;
  }
  _workingWidth: number;

  /**
   * Larghezza totale della griglia (somma della larghezza delle singole colonne). La parte che non ci sta nella viewport diventa scrollabile
   */
  get width(){
    return this._width;
  }
  _width: number;

  /**
   * La larghezza (visibile) effettivamente occupata della larghezza utile (può essere minore o uguale della larghezza utile, mai superiore)
   * Si parla di larghezza dell'area visibile (non conta quindi quella fuori dalla viewport)
   */
  get workingWidthUsedWidth(){
    return this._workingWidthUsedWidth;
  }
  _workingWidthUsedWidth;

  /**
   * La modalità di esecuzione di filtro e ordinamento. 
   * - Se vale 'client' filtro e ordinamento vengono effettuati all'interno dalla grid sul dataset passato in fase di inizializzazione
   * - Se vale 'server' allora la griglia si limiterà ad emettere i valori correnti di filtro e ordinamento, in modo che il consumer
   * possa ri-inizializzare la grid con un nuovo dataset, a seguito dell'applicazione dei criteri di filtro e ordinamento emessi
   * dalla grid.
   */
  get mode() {
    return this._mode;
  }
  _mode: 'client' | 'server' = 'client';


  get availableActions() {
    return this._availableActions
  }
  _availableActions: GridAction[]
  
  get actionsPreconditions() {
    return this._actionsPreconditions
  }
  _actionsPreconditions: {[key: string]: {[key: string]: boolean}} = {}

  get checkableRowsIdsForCurrentAction() {
    return this._checkableRowsIdsForCurrentAction
  }
  _checkableRowsIdsForCurrentAction: any[]


  /************************************************************************************************
   ************************************************************************************************
   *
   * Filter and Sort SECTION
   *
   ************************************************************************************************
   ***********************************************************************************************/

  /** Vale true se la sezione dei filtri è espansa
   */ 
  filtersShown: boolean = false;

  /** Vale true se la colonna di selezione multipla è visibile
   */ 
  selectionColumnShown: boolean = false;

  /**
   * Azione in corso (more button)
   */
  currentAction: 'none' | GridAction = 'none'

  /** Vale true se le righe sono visibili
   */ 
  rowsShown: boolean = false;

  

  /** vale true se si è verificato un errore nel caricamento dei dati (generalmente dovuto
   * ad un problema nella chiamata al REST). La griglia entra in uno stato in cui è necessario
   * ricaricare i dati premendo il bottone "refresh"
   */ 
  _error: boolean = false;
  get error() {return this._error}

  /** vale true se è in corso il caricamento della griglia (non solo degli items)
   */ 
  _loading: boolean = false;
  get loading() {return this._loading}

  /** vale true se ci sono dei filtri in corso di applicazione
   */ 
  _filtering: boolean = false;
  get filtering() {return this._filtering}

  /** vale true se ci sono degli ordinamenti in corso di applicazione
   */ 
  _sorting: boolean = false;
  get sorting() {return this._sorting}

  /** vale true se ci sono degli item in corso di accodamento al dataset della griglia (infinite scroll)
   */ 
  _loadingMoreItems: boolean = false;
  get loadingMoreItems() {return this._loadingMoreItems}

  get filterValuesForInput(){ return this._filterValuesForInput}
  _filterValuesForInput: {[key: string]: {mode:'equal'|'between',equal:any,from:any,to:any}} = {};

  /** I valori di partenza dei campi dei filters. Questi modelli devono essere usati
   *  per inizializzare i _filterValuesForInput e servono per poter ripristinare all'occorrenza i valori iniziali
   */
  _originalFilterCriteria: {[key: string]: {mode:'equal'|'between',equal:any,from:any,to:any}} = {};

  /** I valori correnti dei campi di filtro. Questi modelli devono raccogliere ogni valore
   *  emesso dai filter field e devono essere usati per filtrare i dati
   */
  _currentFilterCriteria: {[key: string]: {mode:'equal'|'between',equal:any,from:any,to:any}} = {};

  /** Contiene il contatore dei filtri correntemente utilizzati
   *  
   */
  activeFiltersCounter: number = 0;

  /** Valorizzato solo in caso di mode 'CLIENT'
   *  contiene le funzioni di filtro da applicare
   */
  _clientSideFilterFunctions: {[columnId: string]: Function};



  get sortCriterion(){
    return this._currentSortCriterion
  }
  /** Il criterio di filtro di partenza. Deve essere usato per inizializzare
   *  _currentSortCriterion e serve per poter ripristinare all'occorrenza il valore originale
   */
  private _originalSortCriterion: {columnId: string, direction: 'asc' | 'desc'};
  private _currentSortCriterion: {columnId: string, direction: 'asc' | 'desc'};

  filtersModified: boolean = false;







  

  
  /************************************************************************************************
   ************************************************************************************************
   *
   * Footer SECTION
   *
   ************************************************************************************************
   ***********************************************************************************************/

   /** Da usare solo in caso di mode=='server'
    * Il numero di righe viene contato dal parent component e settato in input.
    */
   _rowCount: number;
   get rowCount() {return this._rowCount};





  /************************************************************************************************
   ************************************************************************************************
   *
   * Grid SECTION
   *
   ************************************************************************************************
   ***********************************************************************************************/

  /**
   * Numero di righe visualizzate
   */
  _size: number;
  get size(){
    return this._size;
  }

  @Input() set columns(columns: {[key: string]: GridColumn}){
    if(columns){
      let tmp = JSON.parse(JSON.stringify(columns));
      let width: number = 0;
      this.Object.keys(columns).forEach(el=> {
          let column=columns[el]; 
          if(column.type==undefined) column.type=FieldType.STRING; 
          if(column.hidden==undefined) column.hidden=false; 
          column.label = column.label;
          if(column.sortable==undefined) column.sortable=false;
          if(column.width==undefined) {
            column.width=column.type==FieldType.DATE ? '130px' 
                      : column.type == FieldType.TIME ? '120px' 
                      : '200px'; 
          }
          if(!column.hidden)
            width+=Number(column.width.split('px').join(''));
          tmp[el]=column;
        } 
      )
      //Aggiunge in prima posizione la colonna speciale contenente la checkbox di selezione multipla
      tmp = {...{selection: {type: FieldType.CHECKBOX, width: '40px'}, ...tmp}}

      this._columns = tmp
      this._width=width;
      if(!this._workingWidth)
        this._workingWidth = this._width;
      this._calcWorkingWidthUsedWidth()
      this.eventEmission.emit({widthChange: this._width})
    }
  };
  get columns(){
    return this._columns
  }
  _columns: {[key: string]: GridColumn} = {}


  @Input() set settings(settings: GridSettings){
    if(settings) {
      this._mode = settings.mode || 'client';
      //In caso di mode 'server' attiva la funzione di INFINITE SCROLL
      if(this.mode == 'server') {

        this._subscription.add(this._injector.get(ScrollDispatcher).scrolled().subscribe(event => {
          //if(this.virtualScroll.getRenderedRange().end === this.virtualScroll.getDataLength()) //quando il range visualizzato comprende l'ulitmo elemento caricato (es. getDataLength = 50 filtro è soddisfatto per range {35:50}, {36:50}, {37,50})
          if(this.virtualScroll.measureScrollOffset('bottom') === 0){
            console.log('xxx new result append - loadingMoreItems? : ',this.loadingMoreItems);
            if(this.rowCount>1000)
              this._injector.get(NotificationDataService).openSnackBar(this._injector.get(TranslateService).instant('GRID.MAX_LIMIT_OF_LOADED_ITEMS_REACHED'))
            // if(!this.loadingMoreItems)
            //   this.onMoreItemsRequest()
          }
        }))
      }
      this._size = settings.size || 8;

      this._settings = settings.columnsSettings;

      if(settings.filterCriteria){
        this._originalFilterCriteria = JSON.parse(JSON.stringify(settings.filterCriteria))
        this._filterValuesForInput = JSON.parse(JSON.stringify(settings.filterCriteria))
      }

      if(settings.sortCriterion){
        this._originalSortCriterion = JSON.parse(JSON.stringify(settings.sortCriterion))
        this._currentSortCriterion = JSON.parse(JSON.stringify(settings.sortCriterion))
      }

      if(settings.availableActions) {
        //TODO: verificare se è necessario clonare e se si può fare diversamente per non perdere le function nell'object
        this._availableActions = settings.availableActions  //JSON.parse(JSON.stringify(settings.availableActions))
      }
    }
  }
  get columnsSettings() { return this._settings }
  _settings: {[key: string]: FormFieldSettings} = {}

  /**
   * Righe settate dal consumer.
   * 
   * - Se mode=='client' si tratta dell'intero resultset, che verrà filtrato e ordinato direttamente sulla grid
   *   In questo caso il setter non verrà più chiamato a meno che non venga ricaricata la pagina
   * 
   * - Se mode=='server' si tratta della parte di dati già recuperati dal server (a blocchi paginati) applicando i criteri di filtro/ordinamento correnti.
   *   In questo caso il sertter verrà chiamato ogni volta che vengono cambiati un criterio di filtro o ordinamento
   *   Le rows ricevute vengono immediatamente clonate su _shownRows per popolare la grid
   */
  @Input() set rows(rows: {[key: string]: number | string | boolean | (string |number)[]  | File | File[]}[]){
    console.log(`%clifecycle - GridComponent.set rows(${rows?'#'+rows.length:''})`, 'color: red;');
    if(rows){
      this._rows = rows;
      {// per ciascuna riga valuta le precondizioni per ciascuna azione della griglia
        this._rows.forEach(el => {
          if(this._availableActions) {
            this._availableActions.forEach(action => {
              if(action.multipleSelectionBased){
                const id: string = el.id as string;
                this._actionsPreconditions[id] = this._actionsPreconditions[id]||{};
                this._actionsPreconditions[id][action.id]=action.precondition(el)
              }
            });
          }
        });
      } 
      if(this.mode == 'client')
        this._applyCurrentSearchCriteriaClientSide() //agisce internamente sulle rows (non c'è interazione con l'esterno)
      else
        this._shownRows = JSON.parse(JSON.stringify(rows)); //in questo caso le righe sono già filtrate e ordinate a monte
    }
  }
  private _rows: {[key: string]: number | string | boolean | (string |number)[]  | File | File[]}[]

 /**
   * Righe visualizzate.
   * 
   * - Se mode=='client' puo' non coincidere con 'rows', perchè 'shownRows' è filtrato e ordinato
   * - Se mode=='server' coincide con 'rows'
   */
  get shownRows(){
    return this._shownRows;
  }
  private _shownRows: {[key: string]: number | string | boolean | (string |number)[]  | File | File[]}[]

  /**
   * Riga selezionata (tramite click)
   */
  _selectedRowId
  get selectedRowId(){
    return this._selectedRowId
  }

  /**
   * Righe selezionate (checkbox selezione multipla)
   */
  _checkedRowsIds=[]
  get checkedRowsIds(){
    return this. _checkedRowsIds
  }

  @ViewChild(CdkVirtualScrollViewport) virtualScroll: CdkVirtualScrollViewport;

  searchingAnimationIndex;

  Object = Object
  Array = Array
  formatInteger = formatInteger
  serverSideGridMaxRowNum = serverSideGridMaxRowNum

  constructor(
    public changeDetectorRef: ChangeDetectorRef,
    public formFieldDataService: FormFieldDataService,
    public securityDataService: SecurityDataService,
    protected _translator: TranslateService,
    protected _injector: Injector) {
      super(_injector);

      this.searchingAnimationIndex=0;
      setInterval(()=>{
        if(this.searchingAnimationIndex >= this.size-1)
          this.searchingAnimationIndex = 0  
        else
          this.searchingAnimationIndex++
        this.changeDetectorRef.detectChanges();
      },100)
  }

  rowClick = (row: {[key: string]: number | string | boolean | (string |number)[]  | File | File[]}) => {
    this._selectedRowId = row.id;
    this.eventEmission.emit({rowClick: row});
  }

  toggleGlobalCheck(checked: boolean){
    const startingFromIndeterminate = this.checkedRowsIds.length>0 && this.shownRows.length!=this.checkedRowsIds.length
    if(checked)
      this._checkedRowsIds=this.shownRows
      .filter(el=>{
        const currentActionPreconditionSatisfied = this.currentAction != 'none' ? this.actionsPreconditions[el.id as string][this.currentAction.id] : false;
        return currentActionPreconditionSatisfied == true;
      })
      .map(el=>el.id)
    else 
      this._checkedRowsIds=[]
    this.eventEmission.emit({checkedRowsChange:this.checkedRowsIds})
  }

  modifyCheckedRowsIds(rowId, checked: boolean){
    if(checked)
      this._checkedRowsIds.push(rowId)
    else 
      this._checkedRowsIds=this.checkedRowsIds.filter(el=>el!=rowId)
    this.eventEmission.emit({checkedRowsChange:this.checkedRowsIds})
  }

  public async refresh() {
    console.log('%clifecycle - GridComponent.refresh()', 'color: red;');
    if(this.mode == 'client') {
      this.eventEmission.emit({refreshRequest: true})
      this._applyCurrentSearchCriteriaClientSide();
    }
    else
      this._applyCurrentSearchCriteriaServerSide();
  }

  public async clearFilters() {
    console.log('%clifecycle - GridComponent.clearFilters()', 'color: red;');
    const clearSpecialString = 'CLEANING AT '+new Date().toISOString();
    const candidateFilters={}
    await asyncForEach(Object.keys(this.columns), el=> {
      candidateFilters[el] = {
        mode: 'equal', equal: clearSpecialString, from: clearSpecialString, to: clearSpecialString
      }
    })
    this._filterValuesForInput = {}
    this.changeDetectorRef.detectChanges()
    this._filterValuesForInput = candidateFilters;
    this.changeDetectorRef.detectChanges()
  }
  
  public resetFilters() {
    console.log('%clifecycle - GridComponent.resetFilters()', 'color: red;');
    this.clearFilters()
    this.changeDetectorRef.detectChanges()
    setTimeout(()=>{
      this._filterValuesForInput=JSON.parse(JSON.stringify(this._originalFilterCriteria))
      this.changeDetectorRef.detectChanges()
    },1)//senza questo timeout l'applicazione dei filtri originali non ha effetto
  }

  public changeSort(columnId: string) {
    console.log('%clifecycle - GridComponent.changeSort()', 'color: red;');
    if(this.columns[columnId].sortable){
      if(this._currentSortCriterion && this._currentSortCriterion.columnId==columnId){
        if(this._currentSortCriterion.direction=='asc')
          this._currentSortCriterion.direction = 'desc'
        else if(this._currentSortCriterion.direction=='desc')
          this._currentSortCriterion=null
      }else if(!this._currentSortCriterion || this._currentSortCriterion.columnId!=columnId) {
        this._currentSortCriterion={
          columnId: columnId,
          direction: 'asc'
        }
      }
    
      if(this.mode == 'client') {
        let sortedList: {[key: string]: number | string | boolean | (string |number)[]  | File | File[]}[] = JSON.parse(JSON.stringify(this._shownRows))
        this._sortClientSide(sortedList);
        //console.log('!!! grid - filterAndSort - assegnazione shownRows :', JSON.stringify(sortedList) );
        this._shownRows=sortedList
      }else
        this.eventEmission.emit({searchCriteriaChange: {sort: this._currentSortCriterion, filter: this._currentFilterCriteria}})
    }
  }

  public hideSelectionColumn(){
    console.log('%clifecycle - GridComponent.hideSelectionColumn()', 'color: red;');
    if(this.selectionColumnShown){
      this.selectionColumnShown = false;
      this._width-=40;
      this._calcWorkingWidthUsedWidth()
      this.eventEmission.emit({widthChange: this.width})
    }
  }

  public showSelectionColumn(){
    console.log('%clifecycle - GridComponent.showSelectionColumn()', 'color: red;');
    if(!this.selectionColumnShown){
      this.selectionColumnShown = true;
      this._width+=40;
      this._calcWorkingWidthUsedWidth()
      this.eventEmission.emit({widthChange: this.width})
    }
  }

  public setCurrentAction(currentAction: GridAction) {
    console.log('%clifecycle - GridComponent.setCurrentAction() - '+currentAction.id, 'color: red;');
    if(currentAction.multipleSelectionBased)
      this.showSelectionColumn()
    this.currentAction = currentAction;
    this._checkableRowsIdsForCurrentAction = this._shownRows.filter(el=>this._actionsPreconditions[el.id as string][(this.currentAction as GridAction).id]).map(el=>el.id)
    this.eventEmission.emit({actionChange: currentAction});
  }

  /**
   * Questa funzione viene chiamata quando cambia lo stato di un filterField (cambio valore, focus, blur).
   * Se è cambiato il valore di un filtro (quindi non si è trattato solamente di un focus o blur):
   * - Se mode=='client' viene applicato il filtro corrente.
   * - Se mode=='server' viene emesso il valore del filtro corrente, in modo che il consumer possa applicarlo e popolare la grid con un nuovo dataset
   */
  public updateCurrentFilterState(fieldId: string, event: FilterFieldState) {
    console.log(`lifecycle - GridComponent.updateCurrentFilterState(fieldId: ${fieldId}, value: ${((event&&event.value) 
                                                                                                      ? (event.value.mode=='equal' ? event.value.equal : event.value.from +' - '+ event.value.to) 
                                                                                                      : '' 
                                                                                                  )})`);
    const candidateFilterValues = JSON.parse(JSON.stringify(this._currentFilterCriteria))
    candidateFilterValues[fieldId]=event.value
    let isChanged = false;
    if(JSON.stringify(candidateFilterValues) != JSON.stringify(this._currentFilterCriteria)){
      isChanged = true;
      this._currentFilterCriteria = JSON.parse(JSON.stringify(candidateFilterValues))
    }else{
      // console.log('!!! 1 :', JSON.stringify(candidateFilterValues));
      // console.log('!!! 2 :', JSON.stringify(this._currentFilterCriteria));
    }
    if(isChanged) {
      if(this.mode == 'client')
        this._applyCurrentSearchCriteriaClientSide();
      else
        this._applyCurrentSearchCriteriaServerSide();
    }

    this.filtersModified = JSON.stringify(this._originalFilterCriteria)!=JSON.stringify(this._currentFilterCriteria)
  }

  /**
   * In caso di mode=='server' viene utilizzata una infinite-scroll.
   * Quando viene effettuato uno scrollDown deve essere notificato al consumer in modo che richieda al server ulteriori record
   */
  public onMoreItemsRequest() {
    console.log('lifecycle - GridComponent.onMoreItemsRequest()');
    console.log('scrolled down!! - this._rows.length:', this._rows.length);
    this.eventEmission.emit({moreItemsRequest: this._rows.length})
  }

  public onMultipleSelectionToggle(event) {
    console.log('onMultipleSelectionToggle :>> ', event);
  }

  private _calcWorkingWidthUsedWidth = () => this._workingWidthUsedWidth = this.width<this._workingWidth ? this.width : this.workingWidth 

  private _applyCurrentSearchCriteriaServerSide() {
    console.log('%clifecycle - GridComponent._applyCurrentSearchCriteriaServerSide()', 'color: red;');
    this._buildFilterFunctionsAndCountActiveFilters();
    this.eventEmission.emit({searchCriteriaChange: {sort: this._currentSortCriterion, filter: this._currentFilterCriteria}})
  }

  private _applyCurrentSearchCriteriaClientSide() {
    console.log('%clifecycle - GridComponent._applyCurrentSearchCriteriaClientSide()', 'color: red;');
    this._buildFilterFunctionsAndCountActiveFilters();
    if(this._rows) {
      let filteredAndSortedList: {[key: string]: number | string | boolean | (string |number)[]  | File | File[]}[] = JSON.parse(JSON.stringify(this._rows))

      Object.keys(this._clientSideFilterFunctions).forEach(key =>
        filteredAndSortedList = filteredAndSortedList.filter(candidateItem=>this._clientSideFilterFunctions[key](candidateItem[key]))
      );

      this._sortClientSide(filteredAndSortedList);
      
      this._shownRows=filteredAndSortedList
    }
  }

  private _sortClientSide(list: {[key: string]: number | string | boolean | (string |number)[]  | File | File[]}[]) {
    console.log('lifecycle - GridComponent._sort()', JSON.stringify(this._currentSortCriterion));
    if(this._currentSortCriterion){
      list.sort((a, b) => {
        if([FieldType.STRING, FieldType.EMAIL, FieldType.NUMBER, FieldType.INTEGER, FieldType.AMOUNT].includes(this.columns[this._currentSortCriterion.columnId].type)){
          if (a[this._currentSortCriterion.columnId] < b[this._currentSortCriterion.columnId])
            return this._currentSortCriterion.direction == 'asc' ? -1 : this._currentSortCriterion.direction == 'desc' ? 1 : 0
          else if(a[this._currentSortCriterion.columnId] > b[this._currentSortCriterion.columnId])
            return this._currentSortCriterion.direction == 'asc' ? 1 : this._currentSortCriterion.direction == 'desc' ? -1 : 0
          else return 0
        }
      })
    }
  }

  /**
   * cicla su _currentFilterCriteria e
   * 1) [utile solo per mode=='client'] Costruisce una mappa di funzioni di filtro nel formato {[columnId: string]: Function}
   * 2) conta i filtri correnti e aggiorna activeFiltersCounter
   */
  private _buildFilterFunctionsAndCountActiveFilters() {
    let filterFunctions: {[columnId: string]: Function} = {}
    let counter: number = 0;
    Object.keys(this._currentFilterCriteria).forEach(key => {
      if([FieldType.STRING, FieldType.EMAIL, FieldType.PASSWORD].includes(this.columns[key].type)) {
        if(![undefined,null].includes(this._currentFilterCriteria[key].equal)){
          filterFunctions[key]= cellValue => cellValue && cellValue.toLowerCase().includes((this._currentFilterCriteria[key].equal as string).toLowerCase())
          counter++;
        }
        else { //empty filter
          filterFunctions[key]= cellValue => true
        }
      }else if([FieldType.NUMBER, FieldType.INTEGER, FieldType.AMOUNT, FieldType.BOOLEAN].includes(this.columns[key].type)) {
        if(this._currentFilterCriteria[key].mode == 'equal'){
          if(![undefined,null].includes(this._currentFilterCriteria[key].equal)){
            filterFunctions[key]= cellValue => cellValue && cellValue==this._currentFilterCriteria[key].equal
            counter++;
          }else //empty filter
            filterFunctions[key]= cellValue => true
        }else{ //between mode
          if(![undefined,null].includes(this._currentFilterCriteria[key].from) || ![undefined,null].includes(this._currentFilterCriteria[key].to)){
            filterFunctions[key]= cellValue => cellValue && cellValue>=(this._currentFilterCriteria[key].from||-Infinity) && cellValue<=(this._currentFilterCriteria[key].to||Infinity)
            counter++;
          }else //empty filter
            filterFunctions[key]= cellValue => true
        }
      }else if([FieldType.DATE, FieldType.TIME, FieldType.DATETIME].includes(this.columns[key].type)) {
        if(this._currentFilterCriteria[key].mode == 'equal'){
          if(![undefined,null].includes(this._currentFilterCriteria[key].equal)){
            filterFunctions[key]= cellValue => {
              if(!cellValue) //if cell is empty filter doesn't match
                return false;
              let filter;
              if (FieldType.DATE==this.columns[key].type) {
                cellValue = cellValue.slice(0,10)+' 00:00:00'
                filter = this._currentFilterCriteria[key].equal.slice(0,10)+' 00:00:00'
              }
              else if(FieldType.TIME==this.columns[key].type){
                cellValue = '01-01-0001 '+cellValue+':00'
                filter = '01-01-0001 '+this._currentFilterCriteria[key].equal
              }
              else if(FieldType.DATETIME==this.columns[key].type){
                cellValue = cellValue+':00'
                filter = this._currentFilterCriteria[key].equal
              }
              const a = new Date(new Date(cellValue).getFullYear(), new Date(cellValue).getMonth(), new Date(cellValue).getDate(), new Date(cellValue).getHours(), new Date(cellValue).getMinutes(), new Date(cellValue).getSeconds());
              const b = new Date(new Date(filter).getFullYear(), new Date(filter).getMonth(), new Date(filter).getDate(), new Date(filter).getHours(), new Date(filter).getMinutes(), new Date(filter).getSeconds());
              return a.toISOString()==b.toISOString()
            }
            counter++;
          }else //empty filter
            filterFunctions[key]= cellValue => true
        }else{ //between mode
          if(![undefined,null].includes(this._currentFilterCriteria[key].from) || ![undefined,null].includes(this._currentFilterCriteria[key].to)){
            filterFunctions[key]= cellValue => {
              if(!cellValue) //if cell is empty filter doesn't match
                return false;
              const minDate: Date = new Date('0001-01-01 00:00:00')
              const maxDate: Date = new Date('9999-12-31 00:00:00')
              let fromFilter, toFilter;
              if(FieldType.TIME==this.columns[key].type){
                cellValue = '01-01-0001 '+cellValue+':00'
                fromFilter = this._currentFilterCriteria[key].from ? '01-01-0001 '+this._currentFilterCriteria[key].from.slice(0,10) : minDate
                toFilter = this._currentFilterCriteria[key].to ? '01-01-0001 '+this._currentFilterCriteria[key].to.slice(0,10) : maxDate
              } else {
                cellValue = cellValue.slice(0,10)+' 00:00:00'
                fromFilter = this._currentFilterCriteria[key].from ? this._currentFilterCriteria[key].from+' 00:00:00' : minDate
                toFilter = this._currentFilterCriteria[key].to ? this._currentFilterCriteria[key].to+' 00:00:00' : maxDate
              }
              const a = new Date(new Date(cellValue).getFullYear(), new Date(cellValue).getMonth(), new Date(cellValue).getDate(), new Date(cellValue).getHours(), new Date(cellValue).getMinutes(), new Date(cellValue).getSeconds());
              const min: Date = new Date(new Date(fromFilter as string).getFullYear(), new Date(fromFilter as string).getMonth(), new Date(fromFilter as string).getDate(), new Date(fromFilter).getHours(), new Date(fromFilter).getMinutes(), new Date(fromFilter).getSeconds());
              const max: Date = new Date(new Date(toFilter as string).getFullYear(), new Date(toFilter as string).getMonth(), new Date(toFilter as string).getDate(), new Date(toFilter).getHours(), new Date(toFilter).getMinutes(), new Date(toFilter).getSeconds());
              return a>=min && a<=max
            }
            counter++;
          }
          else //empty filter
            filterFunctions[key]= cellValue => true
        }
      }else if([FieldType.OPTION].includes(this.columns[key].type)) {
        if(![undefined,null].includes(this._currentFilterCriteria[key].equal)){
          filterFunctions[key]= cellValue => cellValue && cellValue==this._currentFilterCriteria[key].equal
          counter++;
        }else //empty filter
          filterFunctions[key]= cellValue => true
      }else if([FieldType.MULTI_OPTION].includes(this.columns[key].type)) {
        if(![undefined,null].includes(this._currentFilterCriteria[key].equal)) {
          filterFunctions[key]= cellValue => cellValue && (this._currentFilterCriteria[key].equal as any[]).every(el=>(cellValue as any[]).includes(el))
          counter++;
        } else //empty filter
          filterFunctions[key]= cellValue => true
      }
    });
    this.activeFiltersCounter = counter;
    this._clientSideFilterFunctions = filterFunctions;
  }
}
